<template>
  <div>
    <div v-if="subscriptionDetails || isSubscriptionTrialRunning" class="subscription">
      <b-message>
        {{ $t("firewallNote") }}
        <a href="https://hubs.ly/Q01HlNWp0" target="_blank">{{ $t("firewallDataSheet") }}</a>
      </b-message>
      <b-message class="subtitle mt-4">
        {{ $t("confirmEventCreation") }}
      </b-message>
      <b-field class="has-text-centered" :label="'\xa0'">
        <b-button type="is-primary" :loading="isLoading" @click="saveAndPay">
          {{ $t("confirm") }}
        </b-button>
      </b-field>
    </div>
    <div v-else class="event-based">
      <div>
        <p class="subtitle has-text-centered mt-4">
          {{ $t("summary") }}
        </p>
        <b-message
          v-if="bill.positions.length == 0"
          class="has-text-left"
          style="max-width: 750px; margin-left: auto; margin-right: auto"
          type="is-warning"
        >
          {{ $t("noBillWarning") }}
        </b-message>
        <b-menu class="mb-3" style="max-width: 750px; margin: auto" :activable="false">
          <b-menu-list v-for="(position, key) in bill.positions" :key="key" :label="$t(position.title)">
            <b-menu-item>
              <template slot="label">
                <div class="level">
                  <span style="max-width: 70%">
                    <!-- normal position -->
                    <span v-if="position.amount > 0">
                      {{ position.desc }}
                    </span>
                    <!-- discount -->
                    <span v-else-if="position.amount < 0">
                      <b-icon icon="crown" class="mr-2 has-text-warning" size="is-small"></b-icon>{{ position.desc }}
                    </span>
                    <!-- header -->
                    <span v-else>
                      <b>{{ position.desc }}</b>
                    </span>
                  </span>
                  <span
                    v-if="position.amount != 0"
                    style="font-size: 1.3em"
                    class="is-pulled-right has-text-right"
                    :class="position.amount < 0 ? 'has-text-success has-text-weight-semibold' : ''"
                    >{{ intl.format(position.amount) }}</span
                  >
                </div>
              </template>
            </b-menu-item>
          </b-menu-list>
          <b-menu-list>
            <b-menu-item>
              <template slot="label">
                <div class="divider my-0">
                  {{ $t("sum") }}
                </div>
              </template>
            </b-menu-item>
            <b-menu-item>
              <template slot="label">
                <div class="level">
                  <span style="max-width: 70%">{{ $t("sumNetto") }}</span>
                  <span style="font-size: 1.3em" class="is-pulled-right has-text-right is-bold">{{
                    intl.format(bill.amount)
                  }}</span>
                </div>
              </template>
            </b-menu-item>
            <b-menu-item v-if="bill.vat">
              <template slot="label">
                <div class="level">
                  <span style="max-width: 70%">{{ $t("vatShort") }} - {{ (bill.vat * 100).toFixed(2) }} %</span>
                  <span style="font-size: 1.3em" class="is-pulled-right has-text-right is-bold">{{
                    intl.format(bill.amount * bill.vat)
                  }}</span>
                </div>
              </template>
            </b-menu-item>
            <b-menu-item v-if="bill.vat && bill.amount">
              <template slot="label">
                <div class="level">
                  <span style="max-width: 70%">{{ $t("sumBrutto") }}</span>
                  <span style="font-size: 1.3em" class="is-pulled-right has-text-right has-text-weight-bold">{{
                    intl.format(bill.amount * (1 + bill.vat))
                  }}</span>
                </div>
              </template>
            </b-menu-item>
          </b-menu-list>
        </b-menu>
      </div>
      <div class="has-text-centered">
        <div v-if="editable">
          <b-message
            class="has-text-left"
            style="max-width: 750px; margin-left: auto; margin-right: auto"
            type="is-primary"
          >
            {{ $t("saveAndPayExplained") }}
          </b-message>
          <!-- BILL NOTE -->
          <div style="max-width: 750px; margin-left: auto; margin-right: auto" class="is-clearfix my-6">
            <div class="field has-text-left">
              <b-checkbox v-model="checkBillNote">
                <span class="ml-2 is-block" v-html="$t('billNoteInfo')" />
              </b-checkbox>
            </div>
            <b-field v-if="checkBillNote || billNote" class="has-text-left" :label="$t('billNote')">
              <b-input v-model="billNote" autocomplete="off" required />
            </b-field>
          </div>
          <!-- AGB -->
          <div style="max-width: 750px; margin-left: auto; margin-right: auto" class="is-clearfix my-6">
            <div class="field has-text-left">
              <b-checkbox v-model="checkAgb">
                <span class="ml-2 is-block" v-html="$t('agbCheck')" />
              </b-checkbox>
            </div>
          </div>
          <div style="display: flex; justify-content: center; max-width: 100%; flex-wrap: wrap">
            <b-field class="has-text-left mr-2" :label="$t('paymentMethod')">
              <PaymentTypePicker
                v-model="paymentMethod"
                :organization-id="organizationId"
                :is-subscription-event="isSubscriptionEvent"
                @selectedPaymentMethod="setSelectedPaymentMethod"
              />
            </b-field>
            <b-field class="has-text-left" :label="'\xa0'">
              <b-button
                type="is-primary"
                :loading="isLoading"
                :disabled="isLoading || !checkAgb || (checkBillNote && billNote == '')"
                @click="saveAndPay"
              >
                {{ $t("saveAndPay") }}
              </b-button>
            </b-field>
          </div>
        </div>

        <div v-if="editable" class="divider">
          {{ $t("or") }}
        </div>
        <div>
          <b-message type="is-primary">
            {{ $t("saveAsDraftExplained") }}
          </b-message>
          <b-button
            type="is-light mr-2"
            icon-left="file-document-outline"
            :loading="loadingOffer"
            :disabled="loadingOffer"
            @click="downloadOffer"
          >
            {{ $t("downloadOffer") }}
          </b-button>
          <b-button type="is-primary" :loading="isLoading" :disabled="isLoading" @click="saveAsDraftAndClose">
            {{ $t("saveAsDraftAndClose") }}
          </b-button>
        </div>
      </div>
      <b-modal
        :active.sync="inviteModalActive"
        has-modal-card
        trap-focus
        :destroy-on-hide="false"
        aria-role="dialog"
        aria-modal
      >
        <InvitedParticipantsModal :event-id="eventId" :organization-id="organizationId" />
      </b-modal>
    </div>
  </div>
</template>

<script>
import PaymentTypePicker from "@/components/PaymentTypePicker";
import { functions, db } from "@/firebase";
import { generateBillByData } from "@/client-bill-generator";
import iso from "@/iso-langs";
import InvitedParticipantsModal from "@/components/InvitedParticipantsModal";
import isTrialRunning from "./utils";

export default {
  name: "Billing",
  components: {
    PaymentTypePicker,
    InvitedParticipantsModal,
  },
  props: {
    eventId: {
      type: String,
      default: "",
    },
    organizationId: {
      type: String,
      default: "",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currency: "EUR",
      conditions: {},
      loadingOffer: false,
      inviteModalActive: false,
      checkAgb: false,
      checkBillNote: false,
      firebaseOrg: {},
    };
  },
  firestore() {
    return {
      firebaseOrg: db.collection("organizations").doc(this.$route.params.organizationId),
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    intl() {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: this.bill.currency ? this.bill.currency : "EUR",
      });
    },
    bill() {
      return generateBillByData(this.$store.state.organizationData, this.$store.state.draft);
    },
    subscriptionDetails() {
      const orgData = JSON.parse(JSON.stringify(this.firebaseOrg));
      return orgData?.subscriptionDetails;
    },
    isSubscriptionTrialRunning() {
      const organizationDoc = JSON.parse(JSON.stringify(this.firebaseOrg));

      return isTrialRunning(organizationDoc);
    },
    paymentMethod: {
      get() {
        return this.$store.state.draft.paymentMethod;
      },
      set(value) {
        if (value == "inAdvance" || value == "invoice") {
          this.$store.commit("SET_DRAFT_FIELD", ["paymentMethod", value]);
        } else if (value.id) {
          this.$store.commit("SET_DRAFT_FIELD", ["paymentMethod", "mandate"]);
          this.$store.commit("SET_DRAFT_FIELD", ["mandateId", value.id]);
        }
      },
    },
    billNote: {
      get() {
        return this.$store.state.draft.billNote;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["billNote", value]);
      },
    },
    isSubscriptionEvent: {
      get() {
        return this.$store.state.draft.isSubscriptionEvent;
      },
    },
  },
  watch: {
    billNote: function (newVal) {
      if (newVal.length > 0) {
        this.checkBillNote = true;
      } else {
        this.checkBillNote = false;
      }
    },
    checkBillNote: function (newVal) {
      if (!newVal) {
        this.billNote = "";
      }
    },
  },
  methods: {
    setSelectedPaymentMethod(method) {
      if (method == "invoice") {
        this.$store.commit("SET_DRAFT_FIELD", ["paymentMethod", method]);
      } else if (method.id) {
        this.$store.commit("SET_DRAFT_FIELD", ["paymentMethod", "mandate"]);
        this.$store.commit("SET_DRAFT_FIELD", ["mandateId", method.id]);
      }
    },
    async downloadOffer() {
      this.loadingOffer = true;
      await this.$store.dispatch("saveAsDraft", this.eventId);
      await functions
        .httpsCallable("GenerateEventOffer")({
          organizationId: this.$store.getters.getOrganizationId,
          eventId: this.eventId,
          isConfirmation: false,
          lang: this.locale,
        })
        .then(async (cb) => {
          window.open(cb.data.url);
        })
        .finally(() => {
          this.loadingOffer = false;
        });
    },
    async saveAsDraftAndClose() {
      this.$emit("loading", true);
      await this.$store.dispatch("saveAsDraft", this.eventId);
      this.$emit("loading", false);
      this.$router.push({
        name: "Dashboard",
        params: { organizationId: this.organizationId },
      });
    },
    async saveAndPay() {
      this.$emit("loading", true);
      try {
        const paymentMethod = this.$store.state.draft.paymentMethod;
        if (
          paymentMethod !== "invoice" &&
          paymentMethod !== "mandate" &&
          !this.subscriptionDetails &&
          !this.isSubscriptionTrialRunning
        ) {
          throw new Error("No payment method selected !");
        }

        await this.$store.dispatch("saveAsDraft", this.eventId);
        try {
          await functions.httpsCallable("ValidateEvent")({
            eventId: this.eventId,
            organizationId: this.organizationId,
            lang: this.locale,
            isSubscription: this.subscriptionDetails || this.isSubscriptionTrialRunning,
          });
        } catch (error) {
          console.log("File: Billing.vue, event validation error: ", error);
        }

        this.$router.push({
          name: "EventOverview",
          params: { organizationId: this.organizationId, eventId: this.eventId },
        });
      } catch (error) {
        this.$buefy.toast.open({
          duration: 5000,
          message: this.$t(error.message),
          type: "is-danger",
        });
      } finally {
        this.$emit("loading", false);
      }
    },
    // check that all data is present, valid and logical
    validateData() {
      if (this.name == "") {
        throw "noName";
      }
      if (this.description == "") {
        throw "noDescription";
      }
      if (this.enablePassword) {
        if (this.passwordError) {
          throw "repeatPasswordWrong";
        }
      }
      // DATE & TIME CHECKING
      if (this.eventTimes.length < 1) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: this.$t("needAtLeast1Day"),
        });
        throw this.$t("needAtLeast1Day");
      }
      if (this.langComb.length < 1) {
        throw "needAtLeast1Translation";
      }
      // LANGUAGE & TRANSLATION CHECKING
      // only check lang combs where both sides are filled
      this.langComb
        .filter((langObj) => {
          if (langObj.lang1 == "" || langObj.lang2 == "") {
            return false;
          }
          return true;
        })
        .forEach((langObj) => {
          if (!Object.keys(iso).includes(langObj.lang1) || !Object.keys(iso).includes(langObj.lang2)) {
            throw "invalid iso-639-2/T";
          }
        });
      return true;
    },
  },
};
</script>

<style></style>
